<template>
  <form
    class="s-product-search"
    :class="{ 's-product-search--with-fields': resolveTarget }"
    novalidate
    @submit.prevent="onSubmit"
  >
    <SAutocomplete
      v-model="productId"
      v-model:filter="filter"
      :aria-label="buttonLabel"
      class="s-product-search__autocomplete"
      disable-filter
      :items="items"
      :label="label"
      name="products"
      :placeholder="placeholder"
      :prefix-icon
      :show-valid-state="false"
      suffix-icon=""
    />
    <slot />
    <SButton
      class="s-product-search__search-button"
      color="accent"
      :disabled="asyncStatus === 'loading'"
      suffix-icon="far fa-arrow-right"
      type="submit"
    >
      {{ buttonLabel }}
    </SButton>
  </form>

  <SErrorMessage
    v-if="status === 'error'"
    block
    class="s-product-search__error"
    color="danger"
    :icon="false"
    size="sm"
  />
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

import { createGtmEvent } from "@solvari/argus";
import { SAutocomplete, SButton } from "@solvari/common-fe";
import {
  createPageAlgoliaQuery,
  useAlgolia,
  ziggyUnsafe,
} from "@solvari/common-fe/integrations";
import { useValidationObserver } from "@solvari/common-fe/validation";

import SErrorMessage from "@/atoms/SErrorMessage.vue";

type ProductSearchHit = { slug: string; url: string };
type ProductSearchTarget = { tracking: Record<string, unknown>; url: string };

const props = withDefaults(
  defineProps<{
    buttonLabel: string;
    label?: string;
    pageType?: "CHECKOUT" | "PRODUCT";
    placeholder?: string;
    prefixIcon?: string;
    resolveTarget?: (
      product: ProductSearchHit | undefined,
    ) => Promise<ProductSearchTarget>;
  }>(),
  {
    pageType: "PRODUCT",
  },
);

const filter = ref("");
const productId = ref<string>();

const query = createPageAlgoliaQuery(
  props.pageType === "PRODUCT"
    ? { facetFilters: ["type:PRODUCT", "indexable:true"], hitsPerPage: 8 }
    : { facetFilters: ["type:CHECKOUT"] },
);

const { results, status, asyncStatus } = useAlgolia(filter, query);

const items = computed(() => {
  if (!results.value) {
    return [];
  }
  return results.value.hits.map((item) => ({
    label: item.title,
    value: item.objectID,
    url: item.path,
  }));
});

const fieldsObserver = props.resolveTarget ? useValidationObserver() : null;

watch(productId, () => {
  void createGtmEvent("cross_sell.product_search.submit");
  if (!props.resolveTarget) {
    onFormSubmit();
  }
});

function onSubmit() {
  const { resolveTarget } = props;
  if (!resolveTarget || !fieldsObserver) {
    onFormSubmit();
    return;
  }
  void fieldsObserver.handleSubmit(
    () => void openResolvedTarget(resolveTarget),
  );
}

function findSelectedHit(): ProductSearchHit | undefined {
  if (!filter.value) {
    return undefined;
  }
  const hits = results.value?.hits ?? [];
  const hit = hits.find((item) => item.objectID === productId.value) ?? hits[0];
  return hit && { slug: hit.slug, url: hit.path };
}

async function openResolvedTarget(
  resolveTarget: NonNullable<typeof props.resolveTarget>,
) {
  const isSearched = !!filter.value;
  // Open before await: popups need the click's user activation.
  const tab = window.open("", "_blank");
  const hit = findSelectedHit();
  let target: Awaited<ReturnType<typeof resolveTarget>>;
  try {
    target = await resolveTarget(hit);
  } catch (error) {
    tab?.location.assign(hit?.url ?? ziggyUnsafe("mainSite.productOverview"));
    throw error;
  }
  if (isSearched) {
    void createGtmEvent("cross_sell.product_search.searched", target.tracking);
  }
  tab?.location.assign(target.url);
}

function openProductOverview() {
  window.open(ziggyUnsafe("mainSite.productOverview"), "_blank")?.focus();
}

function onFormSubmit() {
  if (!filter.value) {
    openProductOverview();
    return;
  }
  void createGtmEvent("cross_sell.product_search.searched");
  const selectedProduct =
    items.value.find((product) => product.value === productId.value) ??
    items.value[0];

  if (!selectedProduct) {
    openProductOverview();
    return;
  }

  window.open(selectedProduct.url, "_blank")?.focus();
}
</script>

<style lang="postcss">
.s-product-search {
  @apply flex w-auto flex-col gap-2 sm:flex-row sm:items-end sm:gap-3.5;

  &__search-button {
    @apply h-12 w-full sm:w-auto;
  }

  &__autocomplete {
    @apply flex-1;
  }

  &__error {
    @apply mt-2 w-fit;
  }

  &--with-fields {
    @apply sm:flex-col sm:items-stretch sm:gap-2;

    .s-product-search__search-button {
      @apply sm:w-full;
    }
  }
}

@screen 800 {
  .s-product-search--with-fields {
    @apply flex-row items-start gap-3.5;

    .s-product-search__search-button {
      /* 2rem = SLabel's 1.5rem line height + the 0.5rem label gap */
      @apply mt-8 w-auto;
    }

    .s-product-search__field {
      /* Fixed: w-min collapses the input to the width of a one-word label */
      @apply w-40 shrink-0;

      .s-label {
        @apply whitespace-nowrap;
      }
    }
  }
}
</style>
